import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_BASE } from '../../constants/general.constants';
import type {
  CreateGrantRequest,
  MemberScopes,
  MyVisibility,
  ProjectCapabilityGrant,
} from '../../interfaces/capability-grant.interface';

/**
 * The data boundary for per-resource capability grants (EHQ-187).
 *
 * Deliberately thin: every method is one request and returns the server's shape
 * unchanged. Mapping into a view model lives in `project-access.mapper.ts` so it
 * can be unit-tested without HTTP, and the single `firstValueFrom` per call is
 * the only place RxJS meets this feature — stores and components are signals
 * throughout.
 *
 * Server: `server/src/api/projects/grant_handlers.rs`.
 */
@Injectable({ providedIn: 'root' })
export class CapabilityGrantService {
  readonly #http = inject(HttpClient);

  /**
   * Every grant in a project, or one member's when `userId` is given.
   *
   * Includes revoked rows, so an admin can see what was withdrawn as well as
   * what is live — callers that only want live authority filter on `disabled`.
   * Admin-gated.
   */
  async list(projectId: number, userId?: number): Promise<ProjectCapabilityGrant[]> {
    const query = userId === undefined ? '' : `?userId=${userId}`;
    return firstValueFrom(
      this.#http.get<ProjectCapabilityGrant[]>(`${API_BASE}/project/${projectId}/grants${query}`)
    );
  }

  /**
   * The caller's own grants. `Read`-gated rather than admin-gated, so an
   * operator can answer "why can't I run this?" without holding the authority
   * the answer is about.
   */
  async listMine(projectId: number): Promise<ProjectCapabilityGrant[]> {
    return firstValueFrom(
      this.#http.get<ProjectCapabilityGrant[]>(`${API_BASE}/project/${projectId}/grants/mine`)
    );
  }

  /**
   * The caller's effective view scoping — which workflows they may see, and
   * whether scoping restricts them at all.
   *
   * Needed alongside {@link listMine} because the grant rows alone cannot say:
   * an admin is exempt from scoping even with grants on file, and a restricted
   * member with no grants sees nothing where an empty list would read as
   * "unrestricted".
   */
  async myVisibility(projectId: number): Promise<MyVisibility> {
    return firstValueFrom(
      this.#http.get<MyVisibility>(`${API_BASE}/project/${projectId}/visibility/mine`)
    );
  }

  /** Grants one capability on one resource. Idempotent — re-issuing a revoked grant re-enables it. */
  async create(projectId: number, grant: CreateGrantRequest): Promise<ProjectCapabilityGrant> {
    return firstValueFrom(
      this.#http.post<ProjectCapabilityGrant>(`${API_BASE}/project/${projectId}/grants`, grant)
    );
  }

  /**
   * Grants many capabilities in one request — what check-all sends.
   *
   * All or nothing server-side: a batch naming one bad routine writes none of
   * it, so a failure needs no partial-state reconciliation here.
   */
  async createBatch(
    projectId: number,
    grants: CreateGrantRequest[]
  ): Promise<ProjectCapabilityGrant[]> {
    return firstValueFrom(
      this.#http.post<ProjectCapabilityGrant[]>(`${API_BASE}/project/${projectId}/grants/batch`, {
        grants,
      })
    );
  }

  /**
   * Revokes a grant.
   *
   * The row is disabled rather than deleted, so the delegation stays auditable.
   * A revoked grant that a scheduled routine runs on takes effect at that
   * routine's next fire.
   */
  async revoke(projectId: number, grantId: number): Promise<void> {
    await firstValueFrom(
      this.#http.delete<void>(`${API_BASE}/project/${projectId}/grants/${grantId}`)
    );
  }

  /**
   * Sets a member's view and edit scopes.
   *
   * Both axes go together because the server writes them together — it offers no
   * per-axis update, so sending one would silently reset the other. The server
   * refuses to restrict the project owner or an admin membership; the UI does
   * not offer those combinations, so a rejection here means the membership
   * changed underneath us.
   */
  async setMemberScopes(projectId: number, userId: number, scopes: MemberScopes): Promise<void> {
    await firstValueFrom(
      this.#http.put<void>(`${API_BASE}/project/${projectId}/users/${userId}/scopes`, scopes)
    );
  }
}
